import isNull from 'lodash/isNull'
import { distinctUntilChanged, takeWhile } from 'rxjs'

import { FloorLevelBreaklineTool } from '../floorLevelBreakline/FloorLevelBreakline.tool'
import Base from '../paperTool/PaperTool'
import { convertAbsoluteDistanceToFormattedString } from '../../../../../../components/markup/utils/helpers'
import { Coordinate } from '../../../../../../models/activeDrawable'
import { PRIMARY_200, PRIMARY_300 } from '../../../../../../shared/constants/colors'
import { DRAWABLE_TYPES, DRAWING_TYPES, PAPER_ELEMENT_TYPE } from '../../../../../../shared/constants/drawable-types'
import IndexableObject from '../../../../../../shared/constants/general-enums/indexableObject'
import {
    applyScaleFactorToPathArea,
    applyScaleFactorToPathLength,
    convertScaleFactorLabelEnumToDecimal,
} from '../../../../../../utils/calculations/scaleConversion/scaleConversion'
import { selectIsConflictResolutionInProgress } from '../../../../../slices/masterSetPlan'
import {
    AnalyticsActionEdgeType,
    AnalyticsActionType,
    Cursors,
    EditHandleData,
    IMUP2DCoordinatesToUpdate,
    IMUPState,
    MidpointData,
    MouseButtonCodes,
    MouseButtonsCodes,
    PaperToolConfig,
    TOOL_TYPE_ENUMS,
    TransformationTypes,
    VIEW_MODE,
} from '../../../../../types'
import { SNAPPING_TOLERANCE } from '../../../../../utils/constants'
import { getSnapPoint, getSnapPosition, pathsIntersect } from '../../../../utils/Snapping'

/**
 * Select.tool.tsx
 * Selects an item upon mouse click by default
 * Also contains functions to select all elements and move elements on drag
 */
interface ItemClickAwaitingMouseUp {
    item: paper.Item
    startPosition: paper.Point
    wasActive: boolean
}

export class Select extends Base {
    static NAME = 'SELECT'

    // Percentage of handle radius to use for crosshair length
    // Makes crosshair 50% of handle radius
    private static readonly HANDLE_CROSSHAIR_SIZE_SCALING = 0.5

    private static readonly MEASUREMENT_TOOLTIP_COLOR_HEX = '#000000'
    private static readonly MULTI_SELECT_RECTANGLE_OPACITY = 0.4 // 40%
    private static readonly MULTISELECT_RECTANGLE_STROKE_WIDTH = 4
    private static readonly MULTISELECT_RECTANGLE_PADDING = 16

    private coordinates: IMUP2DCoordinatesToUpdate | null = null
    private editingActive = false
    private panRecoveryMouseUpListener: ((e: MouseEvent) => void) | null = null
    private isInConflictResolution = false
    private isOverlayLockedPreReview = false
    private conflictResolutionOverlayChunkId: number | null = null
    private selectedItem: paper.Item | null = null
    private handles: paper.Group[] = []
    private midPoint: paper.Group | null = null
    private disableSelectTool = false
    private keyboardSelectedItemIds: number[] | null = null
    protected snappingEnabled = false
    private activeToolObjectId: number | null = null
    private activeHighlightId: number | null = null
    private multiSelectRectangle: paper.Item | null = null
    private itemClickAwaitingMouseUp: ItemClickAwaitingMouseUp | null = null
    private multiSelectGroup: paper.Group | null = null
    private multiSelectGroupSelectorRectangle: paper.Item | null = null
    private multiSelectedItemParents: Record<number, paper.Item> | null = null
    private multiSelectGroupCreationPredicate:
        | ((item: paper.Item, multiSelectRectangle: paper.Item) => boolean)
        | null = null
    private onMultiSelectGroupCreationOrUpdate: ((items: paper.Item[]) => void) | null = null
    private areaHitTestItems: paper.Item[] = []

    private VERTEX_STROKE_COLOR = new this.paper.Color('black')
    private VERTEX_FILL_COLOR = new this.paper.Color('white')
    private MULTISELECT_FILL_COLOR = new this.paper.Color(PRIMARY_200)
    private MULTISELECT_STROKE_RECTANGLE_COLOR = new this.paper.Color(PRIMARY_300)

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Select.NAME
        this.mediator
            .get$()
            .pipe(
                takeWhile(
                    (state: IMUPState) =>
                        state.common.activeMode === VIEW_MODE.Markup2D ||
                        state.common.activeMode === VIEW_MODE.Markup2DFor3D
                ),
                distinctUntilChanged()
            )
            .subscribe((state) => {
                this.coordinates = state['2D'].coordinatesToUpdate
                this.editingActive = state['2D'].editingActive
                this.disableSelectTool = state['tools'].disableSelect
                this.keyboardSelectedItemIds = state['2D'].keyboardMultiSelectedItems
                this.snappingEnabled = state['tools'].snappingEnabled
                this.activeGeometryGroupID = state['geometry'].activeGeometryGroupID
                this.activeToolObjectId = state['tools'].activeToolObjectId
                this.activeHighlightId = state['2D'].activeHighlightId
                this.isInConflictResolution = selectIsConflictResolutionInProgress({ IMUP: state })
                this.isOverlayLockedPreReview =
                    this.isInConflictResolution &&
                    state.masterSetPlan.isOverlaidChunkLocked &&
                    !state.masterSetPlan.areConflictsBeingReviewed &&
                    !state.masterSetPlan.isConflictResolutionComplete
                this.conflictResolutionOverlayChunkId = state['2D'].currentOverlayChunkIds?.length
                    ? state['2D'].currentOverlayChunkIds[state['2D'].currentOverlayChunkIds.length - 1]
                    : null
            })

        this.mediator
            .get$()
            .pipe(
                takeWhile(
                    (state: IMUPState) =>
                        state.common.activeMode === VIEW_MODE.Markup2D ||
                        state.common.activeMode === VIEW_MODE.Markup2DFor3D
                ),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this.scaleHandles()
            })

        // subscribe to changes specifically in activeGeometryGroupID
        this.mediator
            .get$()
            .pipe(
                takeWhile(
                    (state: IMUPState) =>
                        state.common.activeMode === VIEW_MODE.Markup2D ||
                        state.common.activeMode === VIEW_MODE.Markup2DFor3D
                ),
                distinctUntilChanged(
                    (prev, curr) => prev.geometry.activeGeometryGroupID === curr.geometry.activeGeometryGroupID
                )
            )
            .subscribe(() => {
                if (!this.activeGeometryGroupID) {
                    this.selectedItem = null
                }
            })

        // subscribe to changes specifically in activeToolObjectId
        this.mediator
            .get$()
            .pipe(
                takeWhile(
                    (state: IMUPState) =>
                        state.common.activeMode === VIEW_MODE.Markup2D ||
                        state.common.activeMode === VIEW_MODE.Markup2DFor3D
                ),
                distinctUntilChanged((prev, curr) => prev.tools.activeToolObjectId === curr.tools.activeToolObjectId)
            )
            .subscribe(() => {
                if (!this.activeToolObjectId) {
                    this.selectedItem = null
                }
            })

        // subscribe to changes specifically in activeHighlightId
        this.mediator
            .get$()
            .pipe(
                takeWhile(
                    (state: IMUPState) =>
                        state.common.activeMode === VIEW_MODE.Markup2D ||
                        state.common.activeMode === VIEW_MODE.Markup2DFor3D
                ),
                distinctUntilChanged((prev, curr) => prev['2D'].activeHighlightId === curr['2D'].activeHighlightId)
            )
            .subscribe(() => {
                if (!this.activeHighlightId) {
                    this.selectedItem = null
                }
            })
    }

    /**
     * Set the size of the handles by increasing their bounding boxes with new rectangles that are the target size.
     * This method is a work-around since Paper's scaling function only works with % scale factors. See: http://paperjs.org/reference/path/#scale-scale
     */
    private scaleHandles = () => {
        if (this.handles) {
            const targetDiameter = this.calculateHandleRadiusAtCurrentZoom() * 2

            this.handles.forEach((handle) => {
                const oldPosition = handle.position

                handle.bounds = new this.paper.Rectangle(
                    handle.bounds.topLeft,
                    new this.paper.Size(targetDiameter, targetDiameter)
                )
                handle.position = oldPosition
            })
        }
    }

    private isSelectedItemARadiusLine = () => {
        return (
            this.selectedItem?.data?.shapeType === DRAWING_TYPES.RADIUS ||
            this.selectedItem?.data?.parent?.data?.shapeType === DRAWING_TYPES.RADIUS
        )
    }

    private createMultiSelectRectangle = (rectangle: paper.Path.Rectangle, startingPoint: paper.Point) => {
        rectangle.opacity = Select.MULTI_SELECT_RECTANGLE_OPACITY
        rectangle.fillColor = this.MULTISELECT_FILL_COLOR
        rectangle.data.shapeType = DRAWING_TYPES.AREA

        rectangle.data.startX = startingPoint.x
        rectangle.data.startY = startingPoint.y

        if (this.multiSelectRectangle) this.multiSelectRectangle.remove()

        this.multiSelectRectangle = rectangle
    }

    onMouseDrag = (event: paper.ToolEvent): void => {
        this.mediator.mediate('materialFlags', { shouldAdjustFlagDetailsPosition: true })

        // Assign mouse position to the draggable point
        if (this.toolPanning(event) || this.disableSelectTool) {
            return
        }

        if (this.selectedItem) {
            this.selectedItem.data.state = TransformationTypes.MOVING

            !this.editingActive &&
                this.mediator.mediate('2D', {
                    latestAnalyticsAction: {
                        startOrStop: AnalyticsActionEdgeType.START,
                        actionType: AnalyticsActionType.EDIT,
                        coordinates: this.getItemCoordinates(this.selectedItem),
                    },
                })

            !this.editingActive &&
                this.setState('2D', {
                    editingActive: true,
                })
            this.selectedItem.data.shapeHasChanged = true
            if (this.selectedItem.data.vertex) {
                this.moveVertex(this.selectedItem, event)
            } else if (
                this.selectedItem.data.state === TransformationTypes.MOVING &&
                // Opt-out for selectable-but-not-draggable items (e.g. a locked overlay raster).
                this.selectedItem.data.movable !== false
            ) {
                this.moveItem(this.selectedItem, event)
            }
        } else if (this.multiSelectRectangle) {
            const startingPoint = new this.paper.Point(
                this.multiSelectRectangle.data.startX,
                this.multiSelectRectangle.data.startY
            )
            const newMultiSelectRectangle = new this.paper.Path.Rectangle(startingPoint, event.point)

            this.createMultiSelectRectangle(newMultiSelectRectangle, startingPoint)
        } else if (this.multiSelectGroup && this.multiSelectGroup.bounds.contains(event.point)) {
            this.multiSelectGroup.position = this.multiSelectGroup.position.add(event.delta)
            this.updateMultiSelectGroupSelectorRectangle()
        }
    }

    /**
     * Moves the entire PathItem (maintains dimensions and scale)
     * @param item
     * @param event
     */
    private moveItem = (item: paper.Item, event: paper.ToolEvent) => {
        if (
            item.data.drawing_type === DRAWABLE_TYPES.ROOF_SYSTEM ||
            item.data.drawing_type === DRAWABLE_TYPES.ROOF_SYSTEMS ||
            item.data.drawing_type === DRAWABLE_TYPES.FLOOR_SYSTEM ||
            item.data.drawing_type === DRAWABLE_TYPES.FLOOR_SYSTEMS
        ) {
            // Adding sensibility tolerance for joist items as every change triggers api call
            if (Math.abs((event as any).event.movementX) > 2 || Math.abs((event as any).event.movementY) > 2) {
                item.position = item.position.add(event.point).subtract(event.lastPoint)
                this.updateAllHandles(item)
            }
        } else {
            if (this.snappingEnabled && !item.data?.toolName) {
                let closestDistance = Infinity
                let closestSnapPosition: paper.Point | null = null

                const snappingItems = this.getSnappingItems()

                snappingItems.forEach((path) => {
                    if (pathsIntersect(item as paper.Path, path as paper.Path, SNAPPING_TOLERANCE)) {
                        const eventPoint =
                            item.data.shapeType === DRAWING_TYPES.POINT || path.data.shapeType === DRAWING_TYPES.POINT
                                ? event.point
                                : null

                        const calculatedSnapPosition = getSnapPosition(
                            this.paper,
                            path,
                            item,
                            SNAPPING_TOLERANCE,
                            eventPoint
                        )

                        if (!isNull(calculatedSnapPosition)) {
                            const distanceToPath = event.point.getDistance(calculatedSnapPosition)

                            if (distanceToPath < closestDistance) {
                                closestDistance = distanceToPath
                                closestSnapPosition = calculatedSnapPosition
                            }
                        }
                    }
                })

                if (closestSnapPosition) {
                    item.position = closestSnapPosition
                }
            }

            item.position = item.position.add(event.point).subtract(event.lastPoint)
            this.updateAllHandles(item)
        }
    }

    private getSiblingElement(vertex: paper.Item, baseItem: paper.PathItem): paper.Segment | null {
        if (
            baseItem instanceof this.paper.Path &&
            [TOOL_TYPE_ENUMS.MEASUREMENT, DRAWING_TYPES.SECTION].includes(baseItem.data.shapeType) &&
            baseItem.segments
        ) {
            if (baseItem.segments[vertex.data.position + 1]) {
                return baseItem.segments[vertex.data.position + 1]
            }

            return baseItem.segments[vertex.data.position - 1]
        }

        return null
    }

    private getNewPositionOnVertexMove(
        event: paper.ToolEvent,
        baseItem: paper.PathItem,
        siblingSegment: paper.Segment | null
    ): paper.Point {
        const isShiftKeyPressed = event['event'].shiftKey

        const snappingItems = this.getSnappingItems()

        const eventPoint = this.snappingEnabled ? getSnapPoint(snappingItems, event.point) : event.point

        if (
            isShiftKeyPressed &&
            baseItem instanceof this.paper.Path &&
            [TOOL_TYPE_ENUMS.MEASUREMENT, DRAWING_TYPES.SECTION].includes(baseItem.data.shapeType) &&
            siblingSegment
        ) {
            return this.calculateFixedAnglePoint(siblingSegment.point, eventPoint)
        }

        // break line should be resized only horizontally
        if (baseItem.data.toolName === FloorLevelBreaklineTool.NAME) {
            return new this.paper.Point([eventPoint.x, baseItem.position.y])
        }

        return eventPoint
    }

    /**
     * Returns true when the path is a 4-segment rectangle-shaped area.
     */
    private isRectanglePath = (path: paper.Path): boolean =>
        path.segments.length === 4 && path.data.shapeType === DRAWING_TYPES.AREA

    /**
     * Resize the rectangle by moving the dragged corner and the adjacent corner
     * that shares its edge, constrained to the dominant drag axis.
     *
     * Horizontal drag (|dx| >= |dy|): both corners on the same vertical edge move in x.
     * Vertical drag   (|dy|  > |dx|): both corners on the same horizontal edge move in y.
     */
    private resizeRectangleFromVertex = (vertex: paper.Item, path: paper.Path, newPosition: paper.Point): void => {
        const draggedIndex: number = vertex.data.position
        const segments = path.segments
        const originalPoint = segments[draggedIndex].point

        const dx = Math.abs(newPosition.x - originalPoint.x)
        const dy = Math.abs(newPosition.y - originalPoint.y)

        if (dx >= dy) {
            // Horizontal resize — find corner on the same vertical edge (same original x)
            const adjacentIndex = segments.findIndex(
                (seg, i) => i !== draggedIndex && Math.abs(seg.point.x - originalPoint.x) <= 1
            )

            if (adjacentIndex === -1) return

            const constrainedPoint = new this.paper.Point(newPosition.x, originalPoint.y)
            const adjacentPoint = new this.paper.Point(newPosition.x, segments[adjacentIndex].point.y)

            segments[draggedIndex].point = constrainedPoint
            segments[adjacentIndex].point = adjacentPoint
            vertex.position = constrainedPoint
            vertex.nextSibling.position = constrainedPoint
            this.handles[adjacentIndex].position = adjacentPoint
        } else {
            // Vertical resize — find corner on the same horizontal edge (same original y)
            const adjacentIndex = segments.findIndex(
                (seg, i) => i !== draggedIndex && Math.abs(seg.point.y - originalPoint.y) <= 1
            )

            if (adjacentIndex === -1) return

            const constrainedPoint = new this.paper.Point(originalPoint.x, newPosition.y)
            const adjacentPoint = new this.paper.Point(segments[adjacentIndex].point.x, newPosition.y)

            segments[draggedIndex].point = constrainedPoint
            segments[adjacentIndex].point = adjacentPoint
            vertex.position = constrainedPoint
            vertex.nextSibling.position = constrainedPoint
            this.handles[adjacentIndex].position = adjacentPoint
        }
    }

    /**
     * Moves a single point for a PathItem
     * @param vertex
     * @param event
     */
    private moveVertex(vertex: paper.Item, event: paper.ToolEvent) {
        const baseItem = vertex.data.parent

        const siblingSegment = this.getSiblingElement(vertex, baseItem)

        const newPosition = this.getNewPositionOnVertexMove(event, baseItem, siblingSegment)

        if (baseItem instanceof this.paper.Path) {
            if (event['event'].shiftKey && this.isRectanglePath(baseItem)) {
                this.resizeRectangleFromVertex(vertex, baseItem, newPosition)

                this.mediator.mediate('common', {
                    tooltip: {
                        title: `Area: ${applyScaleFactorToPathArea({
                            pxValue: baseItem.area,
                            scaleFactor: this.scaleFactor,
                            dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                            xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                            yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                            pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                        }).toFixed(2)} sqft`,
                        visible: true,
                        color: Select.MEASUREMENT_TOOLTIP_COLOR_HEX,
                    },
                })

                return
            }

            if (baseItem.data.shapeType === DRAWING_TYPES.POINT) {
                baseItem.position = newPosition
                vertex.position = newPosition
                vertex.nextSibling.position = newPosition

                return
            }

            vertex.position = newPosition
            vertex.nextSibling.position = newPosition
            baseItem.segments[vertex.data.position].point = newPosition

            // Only show distance tooltip for a line (2 segments -> 2 points)
            // Radius lines can have >2 segments but are still lines
            if (baseItem.segments.length === 2 || baseItem.data.shapeType === DRAWING_TYPES.RADIUS) {
                // if the user is moving the first point in the segment the new point will be equal
                // to the first point and the calculation could possibly lead to
                // using the wrong calibration factor see: https://portal.myparadigm.com/browse/BLUEPRINTS-21590
                const firstPoint = baseItem.segments.find((seg) => seg.point.subtract(newPosition).length > 0)

                if (!firstPoint) return

                const coordinates: Coordinate[] = [
                    [firstPoint.point.x, firstPoint.point.y],
                    [newPosition.x, newPosition.y],
                ]

                this.mediator.mediate('common', {
                    tooltip: {
                        title: `Length: ${convertAbsoluteDistanceToFormattedString(
                            applyScaleFactorToPathLength({
                                pxValue: baseItem.length,
                                scaleFactor: this.scaleFactor,
                                dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                                xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                                yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                                coordinates: coordinates,
                                pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                            })
                        )}`,
                        visible: true,
                        color: Select.MEASUREMENT_TOOLTIP_COLOR_HEX,
                    },
                })
            } else if (baseItem.segments.length > 2) {
                this.mediator.mediate('common', {
                    tooltip: {
                        title: `Area: ${applyScaleFactorToPathArea({
                            pxValue: baseItem.area,
                            scaleFactor: this.scaleFactor,
                            dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                            xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                            yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                            pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                        }).toFixed(2)} sqft`,
                        visible: true,
                        color: Select.MEASUREMENT_TOOLTIP_COLOR_HEX,
                    },
                })
            }
        } else if (baseItem instanceof this.paper.CompoundPath) {
            // escape the movement of the vertex if it's out of bounds of the cutout's encompassing path
            if (vertex.data.cutoutIndex !== 0 && !baseItem.children[0].contains(newPosition)) return

            this.mediator.mediate('common', {
                tooltip: {
                    title: `Area: ${applyScaleFactorToPathArea({
                        pxValue: baseItem.area,
                        scaleFactor: this.scaleFactor,
                        dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                        xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                        yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                        pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                    }).toFixed(2)} sqft`,
                    visible: true,
                    color: Select.MEASUREMENT_TOOLTIP_COLOR_HEX,
                },
            })

            const allSegments = baseItem.children.flatMap((item) => {
                const path = item as paper.Path

                return path.segments
            })
            const oldPosition = allSegments[vertex.data.position].point // maintain old position in the case that the movement overlaps another cutout

            allSegments[vertex.data.position].point = newPosition // the position needs to be updated so that the geometry test can be done

            // iterate through the cutouts to perform the geometry tests
            for (let i = 0; i < baseItem.children.length; i++) {
                // When moving a parent vertex, only check for self-intersection of the outer path.
                // Cutouts that end up outside the parent are removed on mouse release.
                if (vertex.data.cutoutIndex === 0 && i > 0) continue

                if (baseItem.children[vertex.data.cutoutIndex].intersects(baseItem.children[i])) {
                    allSegments[vertex.data.position].point = oldPosition

                    return
                }
            }

            vertex.position = newPosition
            vertex.nextSibling.position = newPosition
        }
    }

    /**
     * Moves all the handles for a PathItem (primarily used when moving an entire PathItem) to match the item's vertices
     * @param item
     */
    private updateAllHandles(item: paper.Item) {
        if (item instanceof this.paper.Path) {
            if (item.data.shapeType === DRAWING_TYPES.POINT) {
                if (this.handles.length) {
                    this.handles[0].position = item.position
                }

                return
            }

            const handles = this.handles

            handles.forEach((handle: paper.Group, i: number) => {
                handle.position = item.segments[i].point
            })
        } else if (item instanceof this.paper.CompoundPath) {
            const handles = this.handles
            const allSegments = item.children.flatMap((item) => {
                const path = item as paper.Path

                return path.segments
            })

            handles.forEach((handle: paper.Group, i: number) => {
                handle.position = allSegments[i].point
            })
        }
    }

    private getItemCoordinates = (item: paper.Item): Coordinate[] => {
        const itemToUpdate = item.data.vertex || item.data.midpoint ? item.data.parent : item

        return this.convertPaperItemToAPICoordinateModel(itemToUpdate).coordinates
    }

    /**
     * Generates the handles for a PathItem
     * handles = UI elements that are used to visualize & edit vertex points
     * @param item
     */
    private addHandles = (item: paper.Item) => {
        if (item instanceof this.paper.Path) {
            item.segments.forEach((vertex, i) => {
                const handleData: EditHandleData = {
                    vertex: true,
                    parent: item,
                    position: i,
                    ignoreHitFilter: true,
                }
                const handle = this.buildHandle(vertex.point, handleData)

                this.handles.push(handle)
            })
        } else if (item instanceof this.paper.CompoundPath) {
            let segmentIndex = 0 // this is the overall index of the segment within the compound path

            item.children.forEach((subPath, subPathIndex) => {
                const path = subPath as paper.Path

                path.segments.forEach((vertex) => {
                    const handleData: EditHandleData = {
                        vertex: true,
                        parent: item,
                        position: segmentIndex,
                        cutoutIndex: subPathIndex, // denotes a cutout vertex
                        ignoreHitFilter: true,
                    }

                    const handle = this.buildHandle(vertex.point, handleData)

                    this.handles.push(handle)
                    segmentIndex++
                })
            })
        }
    }

    /**
     *
     * @param point vertex point for the
     * @param handleData object for the data for the handle
     * @returns
     */
    private buildHandle = (point: paper.Point, handleData: EditHandleData): paper.Group => {
        const handle = new this.paper.Group()
        const handleRadius = this.calculateHandleRadiusAtCurrentZoom()

        handle.addChild(
            new this.paper.Path.Circle({
                center: point,
                strokeColor: this.VERTEX_STROKE_COLOR,
                fillColor: this.VERTEX_FILL_COLOR,
                radius: handleRadius,
                opacity: 0.6,
                strokeScaling: false,
                data: handleData,
            })
        )

        handle.data = handleData

        const crossHairSize = handleRadius * Select.HANDLE_CROSSHAIR_SIZE_SCALING

        handle.addChild(this.constructCrosshairMarker(point, crossHairSize))
        handle.bringToFront()

        return handle
    }

    private handleMidpointClick = (event) => {
        this.removeMidpoint()

        const pathToDivide: paper.Path =
            event.target.data.parent instanceof this.paper.CompoundPath
                ? event.target.data.curve.path
                : (event.target.data.parent as paper.Path)

        // curve location takes a `time` param that is equivalent to the % (0-1) through the curve. So 0.5 would be the midpoint of the curve.
        pathToDivide.divideAt(new this.paper.CurveLocation(event.target.data.curve, 0.5))

        this.removeHandles()
        this.addHandles(event.target.data.parent)
        this.setState('2D', {
            editingActive: true,
        })
        this.saveGeometry()
    }

    private buildMidpoint = (point: paper.Point, midpointData): paper.Group => {
        // TODO: rename to midpoints
        const midpoint = new this.paper.Group()
        const midpointRadius = this.calculateHandleRadiusAtCurrentZoom()

        midpoint.addChild(
            new this.paper.Path.Circle({
                center: point,
                strokeColor: new this.paper.Color('black'),
                fillColor: new this.paper.Color('black'),
                radius: midpointRadius * 0.5,
                opacity: 1,
                strokeScaling: false,
                data: midpointData,
            })
        )

        midpoint.data = midpointData // attach the metadata
        midpoint.onClick = this.handleMidpointClick // bind the handler method
        midpoint.bringToFront() // bring the item to front so it's not hidden

        return midpoint
    }

    private extractCoordinatesToUpdateAndUpdate = (itemToUpdate: paper.Item) => {
        const dataToUpdate = this.convertPaperItemToAPICoordinateModel(itemToUpdate)
        const updatedCoordinates = dataToUpdate.coordinates
        const updatedCutouts = dataToUpdate.cutouts

        // test if the coordinates have changed
        if (updatedCoordinates.toString() !== this.coordinates?.toString() && itemToUpdate.data.onSave) {
            const updatePayload: IMUP2DCoordinatesToUpdate = {
                coordinates: updatedCoordinates,
                measurements: dataToUpdate.measurements,
                cutouts: updatedCutouts,
            }

            this.mediator.mediate('2D', {
                latestAnalyticsAction: {
                    startOrStop: AnalyticsActionEdgeType.STOP,
                    actionType: AnalyticsActionType.EDIT,
                    coordinates: updatedCoordinates,
                },
            })

            itemToUpdate.data.onSave(updatePayload)
        }
    }

    /**
     * Save updated coordinates to the store if
     * - drawable has been selected
     * - dragging action for vertices is tracked
     * - new coordinates are different from the previous ones
     */
    private saveGeometry = () => {
        if (this.selectedItem && this.editingActive) {
            // make sure the drawable geometry is the one being edited, not the handles
            const itemToUpdate =
                this.selectedItem.data.vertex || this.selectedItem.data.midpoint
                    ? this.selectedItem.data.parent
                    : this.selectedItem

            this.extractCoordinatesToUpdateAndUpdate(itemToUpdate)
        }
    }

    public updateMultiSelectGroupSelectorRectangle = () => {
        if (this.multiSelectGroup) {
            const selectedRectangle = new this.paper.Path.Rectangle(
                this.multiSelectGroup.bounds.expand(Select.MULTISELECT_RECTANGLE_PADDING)
            )

            selectedRectangle.strokeColor = this.MULTISELECT_STROKE_RECTANGLE_COLOR
            selectedRectangle.strokeWidth = Select.MULTISELECT_RECTANGLE_STROKE_WIDTH
            if (this.multiSelectGroupSelectorRectangle) this.multiSelectGroupSelectorRectangle.remove()
            this.multiSelectGroupSelectorRectangle = selectedRectangle
        }
    }

    private createAndEmitMultiSelectGroup = (isCtrlKeyPressed: boolean) => {
        if (this.multiSelectRectangle) {
            this.createMultiSelectGroup(isCtrlKeyPressed)

            if (this.multiSelectGroup && this.multiSelectGroup.children.length === 0) {
                if (this.multiSelectRectangle) {
                    this.multiSelectGroup!.remove()
                    this.multiSelectGroup = null
                }
            }

            if (this.multiSelectGroup) {
                this.updateMultiSelectGroupSelectorRectangle()
            }

            if (this.multiSelectRectangle) {
                this.multiSelectRectangle.remove()
                this.multiSelectRectangle = null
            }

            if (this.onMultiSelectGroupCreationOrUpdate) {
                this.onMultiSelectGroupCreationOrUpdate(this.getMultiSelectedItems())
            }
        }
    }

    /**
     * When a parent vertex of a CompoundPath has been dragged, remove any cutout children
     * whose segments are no longer fully contained within the parent path.
     * Returns true if any cutouts were removed.
     */
    private removeCutoutsOutsideParent = (): boolean => {
        if (!this.selectedItem?.data?.vertex) return false
        if (this.selectedItem.data.cutoutIndex !== 0) return false

        const baseItem = this.selectedItem.data.parent

        if (!(baseItem instanceof this.paper.CompoundPath) || baseItem.children.length <= 1) return false

        const parentPath = baseItem.children[0] as paper.Path
        let removedAny = false

        for (let i = baseItem.children.length - 1; i >= 1; i--) {
            const cutout = baseItem.children[i] as paper.Path
            const fullyContained = cutout.segments.every((seg) => parentPath.contains(seg.point))

            if (!fullyContained) {
                cutout.remove()
                removedAny = true
            }
        }

        if (removedAny) {
            this.removeHandles()
            this.selectedItem = baseItem
            this.addHandles(baseItem)
        }

        return removedAny
    }

    onMouseUp = (event: paper.ToolEvent): void => {
        if (this.currentActiveCursor !== Cursors.AUTO) {
            this.mediator.mediate('common', { cursor: Cursors.AUTO })
        }

        this.mediator.mediate('common', {
            tooltip: { title: '', visible: false, color: Select.MEASUREMENT_TOOLTIP_COLOR_HEX },
        })

        const nativeEvent = (event as any).event as MouseEvent
        const isCtrlKeyPressed = nativeEvent.ctrlKey

        // If the pan button (right/middle) was released:
        // paper.js sets its internal dragging=false on this mouseup, so the eventual
        // left-button mouseup will be silently ignored by paper.js (onMouseUp won't fire).
        // Register a native listener to catch that left release and call saveGeometry.
        const releasedPanButton =
            nativeEvent.button === MouseButtonCodes.Right || nativeEvent.button === MouseButtonCodes.Middle

        if (releasedPanButton) {
            if (nativeEvent.buttons & MouseButtonsCodes.Left && !this.panRecoveryMouseUpListener) {
                this.panRecoveryMouseUpListener = (e: MouseEvent) => {
                    if (e.button !== MouseButtonCodes.Left) return

                    document.removeEventListener('mouseup', this.panRecoveryMouseUpListener!)

                    this.panRecoveryMouseUpListener = null

                    this.applyItemClickIfNotMoved()

                    if (this.multiSelectGroup?.children[0]?.data.onMultiSelectSave) {
                        this.multiSelectGroup.children[0].data.onMultiSelectSave(
                            this.convertGroupChildrenToAPIUpdateData(this.multiSelectGroup)
                        )
                    }

                    this.removeCutoutsOutsideParent()
                    this.saveGeometry()
                    this.createAndEmitMultiSelectGroup(e.ctrlKey)
                }

                document.addEventListener('mouseup', this.panRecoveryMouseUpListener)
            }

            return
        }

        this.applyItemClickIfNotMoved()

        // Save multi-select group coordinates on mouse up (not during drag)
        if (this.multiSelectGroup?.children[0]?.data.onMultiSelectSave) {
            this.multiSelectGroup.children[0].data.onMultiSelectSave(
                this.convertGroupChildrenToAPIUpdateData(this.multiSelectGroup)
            )
        }

        this.removeCutoutsOutsideParent()
        this.saveGeometry()
        this.createAndEmitMultiSelectGroup(isCtrlKeyPressed)
    }

    /**
     * Remove all the handles from the plan
     */
    removeHandles = () => {
        // need to remove children of the group so that it is destroyed within the paperscope
        // only using remove just dereferences the group from the parent, but doesn't remove it from the paperscope
        this.handles.forEach((vertex) => {
            vertex.remove()
        })
        this.handles = []

        // make sure all vertices are removed
        this.paper.project.activeLayer.children
            .filter((ch) => ch.data.vertex)
            .forEach((t) => {
                t.removeChildren()
            })
    }

    private removeHandleFromItem(item: paper.Item): void {
        // remove handle from UI
        this.handles.filter((handle) => handle.data.parent.id === item.id).forEach((vertex) => vertex.remove())

        // update the state
        this.handles = this.handles.filter((handle) => handle.data.parent.id !== item.id)
    }

    /**
     * Remove any midpoint from the plan
     */
    removeMidpoint = () => {
        if (this.midPoint) {
            this.midPoint.remove()
            this.midPoint = null
        }
    }

    private selectItemForEditing = (item: paper.Item): void => {
        if (this.selectedItem && item.data.vertex) {
            this.selectedItem = item as paper.PathItem
        } else {
            // if the selected item is not a Path, revert to the non-editing selection sequence
            // We also do not want to edit NOTE types (but they are not locked because we need to hover on them)
            if (!(item instanceof this.paper.PathItem)) {
                this.removeHandles()

                return
            } else if (item.data.drawing_type === DRAWABLE_TYPES.NOTE) {
                return
            }

            this.selectedItem = item as paper.Path

            // make sure that old vertices are removed if a new selection is being made while another item is currently selected
            if (this.handles.length) {
                this.removeHandles()
            }

            const isPoint = item.data.shapeType === DRAWING_TYPES.POINT

            if (!this.handles.length) {
                if (isPoint && this.isOverlayLockedPreReview) {
                    const handleData: EditHandleData = {
                        vertex: true,
                        parent: item as paper.PathItem,
                        position: 0,
                        ignoreHitFilter: true,
                    }

                    this.handles.push(this.buildHandle(item.position, handleData))
                } else if (!isPoint) {
                    this.addHandles(this.selectedItem)
                }
            }
        }
    }

    private selectItemAndAwaitMouseUp = (item: paper.Item): void => {
        this.toggleItemOnClick(item, true)

        this.itemClickAwaitingMouseUp =
            this.selectedItem === item && item.data.onSelect
                ? { item, startPosition: item.position.clone(), wasActive: Boolean(item.data.isActive) }
                : null
    }

    private applyItemClickIfNotMoved = (): void => {
        const click = this.itemClickAwaitingMouseUp

        this.itemClickAwaitingMouseUp = null

        if (!click || this.disableSelectTool || this.selectedItem !== click.item) return

        const { item, startPosition, wasActive } = click

        if (!item.position.equals(startPosition)) return

        item.data.onSelect?.()

        if (!(wasActive && item.data.isActive === false) && item.data.shapeEditable !== false) {
            this.selectItemForEditing(item)
        }
    }

    protected toggleItemOnClick = (item: paper.Item, isAwaitingMouseUp = false): void => {
        if (!item.data.vertex) {
            this.selectedItem = item as paper.PathItem
            if (this.selectedItem.data.selectable !== false) {
                // on element click, add isSelected to know which element is currently selected in blueprint
                this.selectedItem.data.isSelected = true

                // For regions, we click on the path, but the region data is on the parent group,
                // Therefore, we set the scale to the properties of the region group if a region path is selected
                const scale = item.data.scale ?? item.parent.data.scale

                if (scale && scale !== '') {
                    this.setState('2D', { scaleFactor: convertScaleFactorLabelEnumToDecimal(scale) })
                }

                this.selectedItem.data.ignoreHitFilter = true

                if (!isAwaitingMouseUp) this.selectedItem.data.onSelect()

                if (this.selectedItem.data.shapeEditable !== false) {
                    if (!isAwaitingMouseUp) this.selectItemForEditing(this.selectedItem)
                } else {
                    this.removeHandles()
                }
            } else {
                this.removeHandles()
                this.selectedItem = null
            }
        }
    }

    /**
     * Hit test to see if a midpoint should be drawn
     * Returns boolean of whether the midpoint should be drawn
     */
    drawMidpoint = (event: paper.ToolEvent): void => {
        this.removeMidpoint()
        const hitRadius = this.calculateHandleRadiusAtCurrentZoom() // set tolerance to be the width of a handle

        const hitTestItem = this.selectedItem!.data.vertex ? this.selectedItem?.data.parent : this.selectedItem
        const hitTestResult = hitTestItem.hitTest(event.point, {
            tolerance: hitRadius * 2,
            fill: true,
            stroke: true,
        })
        // determine if the result is a valid item

        if (hitTestResult && this.selectedItem !== null) {
            // getting here means that the hit item is a segment already
            let point: paper.Point | null = null

            let midPointData: MidpointData | null = null

            if (hitTestResult.type === 'fill') {
                // remove the old midpoint if it exists
                this.removeMidpoint()

                // if the hit test is outside the bounds, exit the function
                if (
                    (hitTestResult.item as paper.Path).getNearestPoint(event.point).getDistance(event.point) > hitRadius
                ) {
                    return
                }

                const curve = (hitTestResult.item as paper.Path).getNearestLocation(event.point).curve

                point = curve.getPointAtTime(0.5) // get the midpoint of the curve
                midPointData = {
                    midpoint: true,
                    ignoreHitFilter: true,
                    parent: hitTestResult.item,
                    location: hitTestResult.location,
                    curve,
                    index: curve.index,
                }
            } else if (!this.midPoint) {
                point = hitTestResult.location.curve.getPointAtTime(0.5)

                midPointData = {
                    midpoint: true,
                    ignoreHitFilter: true,
                    parent: hitTestResult.item,
                    curve: hitTestResult.location.curve,
                    location: hitTestResult.location,
                    index: hitTestResult.location.curve.index,
                }
            }

            if (point && midPointData && !this.isSelectedItemARadiusLine()) {
                this.midPoint = this.buildMidpoint(point, midPointData)
            }
        }
    }

    private handleAltSelect = (item: paper.Item): void => {
        this.addHandles(item as paper.PathItem)
        this.mediator.mediate('2D', { selectedPaperIdToDelete: item.id })
    }

    private handleCtrlSelect = (item: paper.Item): void => {
        const currentSelectedItems = this.keyboardSelectedItemIds ? [...this.keyboardSelectedItemIds] : []
        const isItemSelected = currentSelectedItems.includes(item.id)

        const keyboardMultiSelectedItems = isItemSelected
            ? currentSelectedItems.filter((id) => id !== item.id)
            : [...currentSelectedItems, item.id]

        if (isItemSelected) {
            this.removeHandleFromItem(item)
            item.data.isSelected = false
        } else {
            this.addHandles(item as paper.PathItem)
            item.data.isSelected = true
        }

        this.mediator.mediate('2D', { keyboardMultiSelectedItems })
    }

    private handleHotkeyOnMouseDown = (event: paper.ToolEvent, hit: paper.HitResult) => {
        const domEvent = (event as any).event as MouseEvent

        if (hit?.item) {
            if (domEvent.ctrlKey) {
                if (hit?.item?.data?.isMultiSelectable) {
                    if (this.selectedItem) {
                        this.selectedItem?.data?.onDeselect &&
                            this.selectedItem.data.onDeselect({ ctrlKey: domEvent.ctrlKey })
                        this.selectedItem = null
                    }

                    if (this.multiSelectGroup) {
                        this.clearAllMultiSelections()
                    }

                    this.handleCtrlSelect(hit.item)
                }

                return true
            }

            // only for AI suggestions
            if (domEvent.altKey) {
                if (hit?.item?.data?.aiSuggestion?.id) {
                    this.handleAltSelect(hit.item)
                }

                return true
            }
        }

        // prevent unselect items when holding ctrl key
        return domEvent.ctrlKey
    }

    onMouseMove = (event: paper.ToolEvent): void => {
        if (this.disableSelectTool || this.isPanningClick(event)) return

        const nativeEvent = (event as any).event as MouseEvent

        // paper.js resets its drag state on any mouseup (including right/middle button release).
        // When left is still held after a pan-button release, paper.js incorrectly fires
        // onMouseMove instead of onMouseDrag. Recover by delegating back to drag handling.
        // Only do this recovery when panRecoveryMouseUpListener is active — meaning left was
        // already held at the moment the pan button was released. A normal left click + tiny
        // mouse movement must NOT trigger onMouseDrag, or it would move the selected item.
        if (nativeEvent.buttons & MouseButtonsCodes.Left && this.panRecoveryMouseUpListener) {
            this.onMouseDrag(event)

            return
        }

        const isMeasureTool =
            this.selectedItem?.data?.toolName === TOOL_TYPE_ENUMS.MEASUREMENT ||
            this.selectedItem?.data?.parent?.data?.toolName === TOOL_TYPE_ENUMS.MEASUREMENT

        // gate keep the hit test behind reasonable logic since it's an expensive operation
        if (
            this.selectedItem &&
            !isMeasureTool && // do not display midpoint for measure tool
            !this.isSelectedItemARadiusLine() &&
            this.handles &&
            this.handles.length
        ) {
            this.drawMidpoint(event)
        }
    }

    onMouseDown = (event: paper.ToolEvent): void => {
        if (this.isPanningClick(event) || this.disableSelectTool) return

        const hit = this.getPaperScope().project.hitTest(event.point)
        const hitNothing = !hit || (!hit.item?.data.midpoint && !hit.item?.['_callbacks'])

        const areaItemsInside = this.areaHitTestItems.filter((item) => item.bounds.contains(event.point))

        const areaItemClicked =
            areaItemsInside.length > 1
                ? areaItemsInside.filter((item) => {
                      return areaItemsInside.filter((it) => item.id !== it.id && !item.isInside(it.bounds)).length === 0
                  })[0]
                : areaItemsInside[0]

        this.itemClickAwaitingMouseUp = null

        this.setState('2D', {
            editingActive: false,
        })

        // The idea here is that if the user clicks within a multiselect group regardless
        // of where that click landed we should let them edit their multi-select group in peace
        // see: https://portal.myparadigm.com/browse/BLUEPRINTS-18140
        const isInsideMultiSelectGroup = this.multiSelectGroup && this.multiSelectGroup.bounds.contains(event.point)

        if (isInsideMultiSelectGroup) return

        if (hit?.item) {
            // when we click on handle, we should pass parent element to ignore it from snapping
            this.setSnappingItems(hit.item.data?.parent || hit.item)
        }

        const isHandled = this.handleHotkeyOnMouseDown(event, hit)

        // prevent CTRL click reset selections
        if (isHandled && this.keyboardMultiSelectedItems?.length) return

        // prevent click on vertex if there are multiple items selected
        if (hit?.item?.data?.vertex && this.keyboardMultiSelectedItems?.length) {
            return
        }

        if (hit && hit.item && (hit.item.data.onSelect || hit.item?.parent?.data.onSelect)) {
            const shouldPreventToggleItem = this.isShouldPreventToggleItem(hit)

            // prevent select item with different opening group id
            // added check for not in conflict resolution to resolve issues
            // with materials of different groups in conflict resolution not b
            if (!this.isInConflictResolution && shouldPreventToggleItem) return

            const isFormOpen = this.activeGeometryGroupID !== null
            const isMultiSelectActive = this.multiSelectGroup !== null

            if (isFormOpen && !isMultiSelectActive && shouldPreventToggleItem) return

            if (this.multiSelectGroup) {
                this.clearAllMultiSelections()
            }

            // This call will trigger the saga which activates the handles via the selectItem method
            const item = hit.item.data.onSelect ? hit.item : hit.item.parent

            // for conflict resolution if there is a selected item
            // and we are clicking on another item then unselect the current
            // item
            if (this.isInConflictResolution && this.selectedItem) {
                this.exitSelectMode()
            }

            // reset keyboard multi selected item ids when we click on item right after multi selecting
            if (this.keyboardSelectedItemIds?.length) {
                this.mediator.mediate('2D', { keyboardMultiSelectedItems: null })
            }

            if (item.data.opening_group_id === this.activeGeometryGroupID) {
                this.selectItemAndAwaitMouseUp(item)
            } else {
                this.toggleItemOnClick(item)
            }
        } else if (hit && hit.item && hit.item.data.vertex) {
            this.selectItemForEditing(hit.item)
        } else if (
            hitNothing &&
            !this.selectedItem &&
            !this.keyboardSelectedItemIds?.length &&
            !this.multiSelectGroup &&
            !areaItemClicked
        ) {
            const rectangle = new this.paper.Path.Rectangle(event.point, event.point)

            this.createMultiSelectRectangle(rectangle, event.point)
        } else if (
            hitNothing &&
            !this.selectedItem &&
            this.multiSelectGroup &&
            !this.multiSelectGroup.bounds.contains(event.point)
        ) {
            this.clearAllMultiSelections()
        } else if (hitNothing && this.selectedItem) {
            this.exitSelectMode()
            this.selectedItem = null
        } else if (hitNothing && this.keyboardSelectedItemIds && this.keyboardSelectedItemIds?.length > 0) {
            this.exitSelectMode()
            this.mediator.mediate('2D', { keyboardMultiSelectedItems: null })
        } else if (areaItemClicked && areaItemClicked.data.onSelect) {
            this.toggleItemOnClick(areaItemClicked)
        }
    }

    cancel = () => {
        this.exitSelectMode()
    }

    /**
     * When this.selectedItem is empty or the selectedItem opening_group_id, it's equal to hit item opening_group_id
     * it's mean that is the same material group and we should allow select, in all other cases we should prevent,
     * when snapping enabled, we see other group materials, other tools e.g., break line tool
     * @param hit
     */
    private isShouldPreventToggleItem = (hit: paper.HitResult): boolean => {
        return !(
            !this.selectedItem ||
            this.selectedItem?.data.opening_group_id === hit?.item?.data?.opening_group_id ||
            this.selectedItem?.data.opening_group_id === hit?.item?.data?.parent?.data?.opening_group_id
        )
    }

    public clearAllMultiSelections = (shouldCallUpdateAction = true) => {
        try {
            this.multiSelectRectangle?.remove()
            this.multiSelectRectangle = null
            const children = [...(this.multiSelectGroup?.children || [])]

            children[0]?.data?.onMultiDeselect && children[0].data.onMultiDeselect()

            if (this.multiSelectGroup && Boolean(this.multiSelectedItemParents)) {
                children?.forEach((item) => {
                    const parent = this.multiSelectedItemParents![item.id]

                    parent.addChild(item)
                    if (parent.parent !== null) {
                        item.sendToBack()
                    } else if (item.data.shapeType === DRAWING_TYPES.AREA) {
                        this.sendItemToBackBeforeRaster(item)
                    }
                })
            }
            this.multiSelectGroup?.remove()
            this.multiSelectGroup = null
            this.multiSelectGroupSelectorRectangle?.remove()
            this.multiSelectGroupSelectorRectangle = null
        } catch (_) {
            // We will catch this error in the case that
            // we have switched pages and dumped data
            // the data from multi selection
            // in that case drop the references
            // to the old data and let it get
            // cleaned up
            this.multiSelectRectangle = null
            this.multiSelectGroup = null
            this.multiSelectGroupSelectorRectangle = null
        }

        this.multiSelectedItemParents = []

        if (shouldCallUpdateAction && this.onMultiSelectGroupCreationOrUpdate) {
            this.onMultiSelectGroupCreationOrUpdate(this.getMultiSelectedItems())
        }
    }

    public exitSelectMode = (): void => {
        this.clearSelections()
    }

    public clearSelections = () => {
        const pathItem = this.selectedItem?.data.onDeselect ? this.selectedItem : this.selectedItem?.data.parent

        if (pathItem?.data) {
            pathItem.data.ignoreHitFilter = false
            pathItem.data?.onDeselect && pathItem.data?.onDeselect()
        }

        if (this.keyboardSelectedItemIds) {
            const keyboardSelectedItems = this.paper.project.getItems({
                id: (id: number) => this.keyboardSelectedItemIds!.includes(id),
            })

            if (keyboardSelectedItems?.length > 0) {
                // usage of the first element is okay, since we can multi select the same type of items only
                keyboardSelectedItems[0].data?.onMultiDeselect && keyboardSelectedItems[0].data?.onMultiDeselect()
            }
        }

        this.removeHandles()
        this.removeMidpoint()

        // on exit select mode, remove isSelected on every selected element on blueprint
        this.unSelectItems()
        this.selectedItem = null
    }

    /**
     * Function gets all selected elements and unselect them,
     * used only to get an element from blueprint
     */
    public unSelectItems = () => {
        const allSelectedUIItems = this.paper.project.getItems({
            data: (data) => data.isSelected,
        })

        if (allSelectedUIItems.length) {
            allSelectedUIItems.forEach((item) => (item.data.isSelected = false))
        }
    }

    public getSelectedItem = () => {
        return this.selectedItem
    }

    private createMultiSelectGroup = (isCtrlKeyPressed: boolean) => {
        if (Boolean(this.multiSelectGroup) || isNull(this.multiSelectRectangle)) return

        const multiSelectGroup = new this.paper.Group()

        this.multiSelectedItemParents = {}

        // MODIFICATION and MODIFICATION_TO_EDIT save/delete differently, so don't mix them in one group.
        let conflictResolutionGroupType: string | null = null

        this.paper.project
            .getItems({
                data: (data: IndexableObject | undefined) => {
                    if (data?.isMultiSelectable && isCtrlKeyPressed) {
                        return data?.type === PAPER_ELEMENT_TYPE.HIGHLIGHT
                    }

                    if (this.isInConflictResolution) {
                        return (
                            data?.isMultiSelectable &&
                            (data?.type === PAPER_ELEMENT_TYPE.MODIFICATION ||
                                data?.type === PAPER_ELEMENT_TYPE.MODIFICATION_TO_EDIT ||
                                (data?.type === PAPER_ELEMENT_TYPE.DRAWABLE && data?.createdDuringConflictResolution))
                        )
                    }

                    return data?.isMultiSelectable && data?.type === PAPER_ELEMENT_TYPE.DRAWABLE
                },
            })
            .forEach((item) => {
                if (
                    this.multiSelectGroupCreationPredicate &&
                    this.multiSelectGroupCreationPredicate(item, this.multiSelectRectangle!)
                ) {
                    if (this.isInConflictResolution) {
                        conflictResolutionGroupType ??= item.data.type
                        if (item.data.type !== conflictResolutionGroupType) return
                    }

                    this.multiSelectedItemParents![item.id] = item.parent
                    multiSelectGroup.addChild(item)
                }
            })

        if (multiSelectGroup.children.length !== 0) {
            this.multiSelectGroup = multiSelectGroup
        }
    }

    public removeItemsWithPredicateFromMultiSelect = (predicate: (item: paper.Item) => boolean) => {
        if (!this.multiSelectGroup) return

        const multiSelectedChildren = this.multiSelectGroup.children.filter((item) => predicate(item))

        this.multiSelectGroup.children.forEach((item) => {
            const parent = this.multiSelectedItemParents![item.id]

            parent.addChild(item)
            if (parent.parent !== null) {
                item.sendToBack()
            } else if (item.data.shapeType === DRAWING_TYPES.AREA) {
                this.sendItemToBackBeforeRaster(item)
            }
        })

        this.multiSelectGroup = new this.paper.Group(multiSelectedChildren)

        this.updateMultiSelectGroupSelectorRectangle()
    }

    public getMultiSelectedItems = () => {
        if (!this.multiSelectGroup) return [] as paper.Item[]

        return this.multiSelectGroup.children
    }

    public getMultiSelectedItemParent = (itemId: number) => {
        if (!this.multiSelectedItemParents) return null

        const potentialParent = this.multiSelectedItemParents[itemId]

        if (!potentialParent) return null

        return potentialParent
    }

    /**
     * Sets the multi selection predicate functions
     */
    public setMultiSelectionMode({
        groupCreationPredicate,
        onMultiSelectCreateOrUpdate,
    }: {
        groupCreationPredicate: (item: paper.Item, rectangle: paper.Item) => boolean
        onMultiSelectCreateOrUpdate: (items: paper.Item[]) => void
    }) {
        this.multiSelectGroupCreationPredicate = groupCreationPredicate
        this.onMultiSelectGroupCreationOrUpdate = onMultiSelectCreateOrUpdate
    }

    public createMultiSelectionGroupFromItems(items: paper.Item[]) {
        if (!isNull(this.multiSelectGroup)) return

        const multiSelectGroup = new this.paper.Group()

        items.forEach((item) => {
            this.multiSelectedItemParents![item.id] = item.parent
            multiSelectGroup.addChild(item)
        })

        if (multiSelectGroup.children.length !== 0) {
            this.multiSelectGroup = multiSelectGroup
        }

        this.updateMultiSelectGroupSelectorRectangle()

        if (this.onMultiSelectGroupCreationOrUpdate) {
            this.onMultiSelectGroupCreationOrUpdate(this.getMultiSelectedItems())
        }
    }

    public getMultiSelectedGroup() {
        return this.multiSelectGroup
    }

    public selectConflictItem = (item: paper.Item | null): void => {
        if (!this.isInConflictResolution) return

        const isSameSelection = item === this.selectedItem

        this.exitSelectMode()
        this.clearAllMultiSelections(true)
        this.selectedItem = null
        if (isSameSelection || !item) return

        item.locked = false
        this.toggleItemOnClick(item)
    }

    public addItemToAreaHitTest(item: paper.Item) {
        this.areaHitTestItems.push(item)
    }

    public resetAreaHitTestItems() {
        this.areaHitTestItems = []
    }

    public removeAreaHitTestItems(ids: number[]) {
        this.areaHitTestItems = this.areaHitTestItems.filter((item) => !ids.includes(item.id))
    }

    public onKeyDown = (keyEvent: any) => {
        const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(keyEvent.key)

        if (!isArrowKey) return

        const shiftVector = this.determinePointToTranslate(keyEvent.event.code)

        if (this.multiSelectGroup && this.multiSelectGroupSelectorRectangle) {
            this.multiSelectGroup.position = this.multiSelectGroup.position.add(shiftVector)
            this.multiSelectGroupSelectorRectangle.position =
                this.multiSelectGroupSelectorRectangle.position.add(shiftVector)
            if (this.multiSelectGroup.children[0].data.onMultiSelectSave) {
                this.multiSelectGroup.children[0].data.onMultiSelectSave(
                    this.convertGroupChildrenToAPIUpdateData(this.multiSelectGroup)
                )
            }
        }
    }

    public extractMultiSelectRectangleWidth = () => {
        if (this.multiSelectGroupSelectorRectangle) return this.multiSelectGroupSelectorRectangle.bounds.width

        return 0
    }
}

export default Select
